import type { HtmlHTMLAttributes } from 'react';

import type { FrontifyImageContent } from '@/contentful/contentful-exports';
import { ViewportSize } from '@/styles/viewportSize';
import { setImgWidth } from '@/utils/frontify';

type ImageSource = [FrontifyImageContent | undefined, number];

export interface ImageSources {
  fallback: ImageSource;
  xs?: ImageSource;
  sm?: ImageSource;
  md?: ImageSource;
  lg?: ImageSource;
  xl?: ImageSource;
}

interface Props extends HtmlHTMLAttributes<HTMLImageElement> {
  images: ImageSources;
  pictureProps?: HtmlHTMLAttributes<HTMLPictureElement>;
  alt: string;
}

function generateSrcSet(source: ImageSource | undefined) {
  if (!source?.[0] || !source[1]) return undefined;
  const [{ preview_url: url }, width] = source;

  return `${setImgWidth(url, width)} 1x, ${setImgWidth(url, width * 2)} 2x, ${setImgWidth(url, width * 3)} 3x`;
}

/*
  ArtDirectedImage is an image utility function that takes a set of images and correctly shows images on diffrent breakpoints. 
  Documentation: https://developer.mozilla.org/en-US/docs/Web/HTML/Guides/Responsive_images#art_direction
*/
export default function ArtDirectedImage(props: Props) {
  const { images, pictureProps, alt, ...rest } = props;

  const fallback = generateSrcSet(images.fallback);
  const xs = generateSrcSet(images.xs);
  const sm = generateSrcSet(images.sm);
  const md = generateSrcSet(images.md);
  const lg = generateSrcSet(images.lg);
  const xl = generateSrcSet(images.xl);

  return (
    <picture style={{ display: 'flex' }} {...pictureProps}>
      {xl && <source media={`(min-width: ${ViewportSize.xl}px)`} srcSet={xl} />}
      {lg && <source media={`(min-width: ${ViewportSize.lg}px)`} srcSet={lg} />}
      {md && <source media={`(min-width: ${ViewportSize.md}px)`} srcSet={md} />}
      {sm && <source media={`(min-width: ${ViewportSize.sm}px)`} srcSet={sm} />}
      {xs && <source media={`(min-width: ${ViewportSize.xs}px)`} srcSet={xs} />}

      <img srcSet={fallback} alt={alt} {...rest} />
    </picture>
  );
}
